import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import DetailsBox from '../../../components/Cards/DetailsBox'
import API from '../../../services/API'
import AddNewWardDepartment from '../../patientTransport/Organisations/AddNewWardDepartment'
import { QuestionMark } from '@mui/icons-material'

const wardLabel = (ward) =>
  [ward?.name, ward?.internalLocation].filter(Boolean).join(' - ')

const portalLevels = ['Ward', 'Site', 'Admin']

const formatTermsAccepted = (access = {}) => {
  if (!access?.termsAcceptedAt || !access?.termsAcceptedVersion) {
    return 'Not accepted'
  }

  return `${access.termsAcceptedVersion} · ${new Date(
    access.termsAcceptedAt,
  ).toLocaleDateString('en-GB')}`
}

const WardEditor = ({ ward, organisation, onClose, onSaved }) => {
  const [form, setForm] = useState(
    ward || {
      name: '',
      type: 'Ward',
      internalLocation: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      isActive: true,
    },
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    const res = await API.put('/pts/warddepartment', {
      ...form,
      warddepartmentID: ward._id,
    })
    if (res.status === 200) {
      onSaved?.()
      onClose?.()
    }
  }

  return (
    <Dialog
      open={!!ward}
      onClose={onClose}
      component="form"
      onSubmit={handleSubmit}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Ward / Department</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Department Name"
            value={form.name || ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            required
            fullWidth
          />
          <TextField
            select
            label="Type"
            value={form.type || 'Ward'}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, type: e.target.value }))
            }
            fullWidth
          >
            {['Ward', 'Emergency Department', 'Discharge Lounge'].map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ),
            )}
          </TextField>
          <TextField
            label="Internal Location"
            value={form.internalLocation || ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, internalLocation: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label="Contact Name"
            value={form.contactName || ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                contactName: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Contact Phone"
            value={form.contactPhone || ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                contactPhone: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Contact Email"
            value={form.contactEmail || ''}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                contactEmail: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            select
            label="Status"
            value={form.isActive === false ? 'Inactive' : 'Active'}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                isActive: e.target.value === 'Active',
              }))
            }
            fullWidth
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const PortalUserEditor = ({ user, organisation, wards, onClose, onSaved }) => {
  const [form, setForm] = useState(
    user || {
      firstName: '',
      lastName: '',
      email: '',
      portalAccessLevel: 'Ward',
      permittedWards: [],
      canLogIn: true,
    },
  )

  const [openAccessLevelHelper, setOpenAccessLevelHelper] = useState(false)
  const effectivePortalAccessLevel =
    form.portalAccessLevel ||
    form.ptsBookingPortalAccess?.portalAccessLevel ||
    'Ward'
  const requiresWardSelection = effectivePortalAccessLevel === 'Ward'

  const selectedWards = useMemo(() => {
    const ids = new Set(
      (form.permittedWards || []).map((ward) => ward?._id || ward),
    )
    return wards.filter((ward) => ids.has(ward._id))
  }, [form.permittedWards, wards])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      portalAccessLevel: effectivePortalAccessLevel,
      permittedWards: requiresWardSelection
        ? selectedWards.map((ward) => ward._id)
        : [],
      canLogIn: form.canLogIn !== false,
    }
    const res = user?._id
      ? await API.put(
          `/pts/organisations/${organisation._id}/portal-users/${user._id}`,
          payload,
        )
      : await API.post(
          `/pts/organisations/${organisation._id}/portal-users`,
          payload,
        )
    if (res.status === 200) {
      onSaved?.()
      onClose?.()
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      component="form"
      onSubmit={handleSubmit}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{user ? 'Edit Portal User' : 'Add Portal User'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First Name"
                  value={form.firstName || ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Last Name"
                  value={form.lastName || ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <TextField
            label="Email"
            type="email"
            value={form.email || ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, email: e.target.value }))
            }
            disabled={!!user}
            required
            fullWidth
          />
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              select
              label="Portal Access"
              value={effectivePortalAccessLevel}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  portalAccessLevel: e.target.value,
                  permittedWards:
                    e.target.value === 'Ward' ? prev.permittedWards : [],
                }))
              }
              fullWidth
            >
              {portalLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>
            <QuestionMark
              sx={{ color: 'contrast.main', cursor: 'pointer' }}
              onClick={() => setOpenAccessLevelHelper(true)}
            />
            <Dialog
              open={openAccessLevelHelper}
              onClose={() => setOpenAccessLevelHelper(false)}
            >
              <DialogTitle>Portal User Access</DialogTitle>
              <DialogContent>
                <Typography variant="h6">Ward</Typography>
                <Typography>
                  Members of staff that are assigned to selected
                  wards/departments and able to book patient transport and view
                  incoming patients
                </Typography>
                <Typography variant="subtitle2">
                  Typically used for ward clerks and nursing staff
                </Typography>

                <Typography variant="h6" mt={2}>
                  Site
                </Typography>
                <Typography>
                  Members of the organisation that are given global access to
                  the platform, can book on behalf of any ward/department, and
                  monitor all journeys in/out of the organisation.
                </Typography>
                <Typography variant="subtitle2">
                  Typically used for hospital site / managers
                </Typography>

                <Typography variant="h6" mt={2}>
                  Admin
                </Typography>
                <Typography>
                  Access to the system for amin only - able to see journeys,
                  view invoices, but unable to create or edit any transport
                  bookings.
                </Typography>
                <Typography variant="subtitle2">
                  Typically used for administration / financial teams
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenAccessLevelHelper(false)}
                  color="anRed"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          {requiresWardSelection ? (
            <Autocomplete
              multiple
              options={wards.filter((ward) => ward.isActive !== false)}
              value={selectedWards}
              getOptionLabel={(option) => wardLabel(option)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event, value) =>
                setForm((prev) => ({ ...prev, permittedWards: value }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Permitted Wards" />
              )}
            />
          ) : (
            <Alert severity="info">
              {effectivePortalAccessLevel} access includes every ward in this
              organisation, so individual ward permissions are not required.
            </Alert>
          )}
          {user && (
            <TextField
              select
              label="Login"
              value={form.canLogIn === false ? 'Disabled' : 'Enabled'}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  canLogIn: e.target.value === 'Enabled',
                }))
              }
              fullWidth
            >
              <MenuItem value="Enabled">Enabled</MenuItem>
              <MenuItem value="Disabled">Disabled</MenuItem>
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const formatLastActive = (value) => {
  if (!value) return 'Never'
  const date = new Date(value)
  const dayMs = 24 * 60 * 60 * 1000
  const daysAgo = Math.floor((Date.now() - date.getTime()) / dayMs)
  if (daysAgo <= 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  if (daysAgo < 30) return `${daysAgo}d ago`
  return date.toLocaleDateString('en-GB')
}

const HospitalPortalAccess = ({ client }) => {
  const [wards, setWards] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [openAddWard, setOpenAddWard] = useState(false)
  const [editWard, setEditWard] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [resendState, setResendState] = useState({})

  const resendInvite = async (event, user) => {
    event.stopPropagation()
    setResendState((prev) => ({ ...prev, [user._id]: 'sending' }))
    try {
      const res = await API.post(
        `/pts/organisations/${client._id}/portal-users/${user._id}/resend-invite`,
      )
      setResendState((prev) => ({
        ...prev,
        [user._id]: res.status === 200 ? 'sent' : 'error',
      }))
    } catch (e) {
      setResendState((prev) => ({ ...prev, [user._id]: 'error' }))
    }
  }

  const loadData = async () => {
    if (!client?._id) return
    setLoading(true)
    const [wardRes, userRes] = await Promise.all([
      API.get(`/pts/organisations/${client._id}/wards`),
      API.get(`/pts/organisations/${client._id}/portal-users`),
    ])
    if (wardRes.status === 200) setWards(wardRes.data)
    if (userRes.status === 200) setUsers(userRes.data)
    setLoading(false)
  }

  useEffect(() => {
    loadData()
  }, [client?._id])

  if (client?.orgType !== 'PTS') return null

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DetailsBox
          title="Hospital Booking Portal"
          caption="Preview the portal experience for this organisation."
          buttonRight={
            <Button
              startIcon={<VisibilityIcon />}
              onClick={() =>
                window.open(
                  `/pts/hospital-portal-preview/${client._id}`,
                  '_blank',
                  'noopener,noreferrer',
                )
              }
              variant="contained"
              size="small"
            >
              View Hospital Portal
            </Button>
          }
        >
          <Alert severity="info">
            Preview ward, site, and admin views using live portal data without
            signing in as a hospital user.
          </Alert>
        </DetailsBox>
      </Grid>
      <Grid item xs={12}>
        <DetailsBox
          title="Wards & Departments"
          caption="Manage the hospital locations available in PTS booking."
          buttonRight={
            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenAddWard(true)}
              variant="contained"
              size="small"
            >
              Add Ward
            </Button>
          }
        >
          <CustomDataGrid
            rows={wards}
            loading={loading}
            columns={[
              {
                field: 'name',
                headerName: 'Ward / Department',
                flex: 1,
                minWidth: 180,
              },
              { field: 'type', headerName: 'Type', flex: 0.7, minWidth: 130 },
              {
                field: 'internalLocation',
                headerName: 'Internal Location',
                flex: 1,
                minWidth: 170,
              },
              {
                field: 'isActive',
                headerName: 'Status',
                minWidth: 110,
                renderCell: ({ row }) => (
                  <Chip
                    label={row.isActive === false ? 'Inactive' : 'Active'}
                    color={row.isActive === false ? 'default' : 'success'}
                    size="small"
                  />
                ),
              },
            ]}
            getRowId={(row) => row._id}
            maxHeight={360}
            disableRowSelectionOnClick
            onRowClick={({ row }) => setEditWard(row)}
            slots={{
              toolbar: () => null,
              footer: () => null,
            }}
          />
        </DetailsBox>
      </Grid>
      <Grid item xs={12}>
        <DetailsBox
          title="Hospital Booking Portal Users"
          caption="Give hospital staff access to specific wards."
          buttonRight={
            <Button
              startIcon={<AddIcon />}
              onClick={() => setEditUser({})}
              variant="contained"
              size="small"
            >
              Add User
            </Button>
          }
        >
          {wards.length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Add at least one ward before assigning ward-level access.
            </Alert>
          )}
          <CustomDataGrid
            rows={users}
            loading={loading}
            columns={[
              {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 170,
                valueGetter: (value, row) =>
                  `${row.firstName || ''} ${row.lastName || ''}`.trim(),
              },
              { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
              {
                field: 'portalAccessLevel',
                headerName: 'Access',
                minWidth: 110,
                valueGetter: (value, row) =>
                  row?.ptsBookingPortalAccess?.portalAccessLevel || 'Ward',
              },
              {
                field: 'permittedWards',
                headerName: 'Wards',
                flex: 1,
                minWidth: 170,
                renderCell: ({ row }) => (
                  <Typography variant="caption">
                    {(row?.ptsBookingPortalAccess?.permittedWards || [])
                      .map(wardLabel)
                      .join(', ') || '—'}
                  </Typography>
                ),
              },
              {
                field: 'emailConfirmed',
                headerName: 'Verified',
                minWidth: 160,
                renderCell: ({ row }) => (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      label={
                        row?.security?.emailConfirmed ? 'Verified' : 'Pending'
                      }
                      color={
                        row?.security?.emailConfirmed ? 'success' : 'warning'
                      }
                      size="small"
                    />
                    {!row?.security?.emailConfirmed && (
                      <Button
                        size="small"
                        startIcon={<ForwardToInboxIcon fontSize="small" />}
                        onClick={(event) => resendInvite(event, row)}
                        disabled={resendState[row._id] === 'sending'}
                        color={
                          resendState[row._id] === 'error'
                            ? 'error'
                            : resendState[row._id] === 'sent'
                              ? 'success'
                              : 'primary'
                        }
                        sx={{ minWidth: 0, textTransform: 'none' }}
                      >
                        {resendState[row._id] === 'sent'
                          ? 'Sent'
                          : resendState[row._id] === 'error'
                            ? 'Retry'
                            : 'Resend'}
                      </Button>
                    )}
                  </Stack>
                ),
              },
              {
                field: 'lastActive',
                headerName: 'Last Active',
                minWidth: 110,
                valueGetter: (value, row) => row?.lastActive || null,
                renderCell: ({ row }) => (
                  <Typography
                    variant="caption"
                    color={row?.lastActive ? 'text.primary' : 'text.secondary'}
                  >
                    {formatLastActive(row?.lastActive)}
                  </Typography>
                ),
              },
              {
                field: 'termsAccepted',
                headerName: 'Terms',
                minWidth: 220,
                valueGetter: (value, row) =>
                  formatTermsAccepted(row?.ptsBookingPortalAccess),
              },
              {
                field: 'edit',
                headerName: '',
                sortable: false,
                filterable: false,
                width: 60,
                renderCell: ({ row }) => (
                  <Button size="small" onClick={() => setEditUser(row)}>
                    <EditIcon fontSize="small" />
                  </Button>
                ),
              },
            ]}
            getRowId={(row) => row._id}
            maxHeight={360}
            disableRowSelectionOnClick
            onRowClick={({ row }) => setEditUser(row)}
            slots={{
              toolbar: () => null,
              footer: () => null,
            }}
          />
        </DetailsBox>
      </Grid>
      <AddNewWardDepartment
        open={openAddWard}
        onClose={() => setOpenAddWard(false)}
        organisation={client}
        afterAdd={() => {
          setOpenAddWard(false)
          loadData()
        }}
      />
      {editWard && (
        <WardEditor
          ward={editWard}
          organisation={client}
          onClose={() => setEditWard(null)}
          onSaved={loadData}
        />
      )}
      {editUser && (
        <PortalUserEditor
          user={editUser?._id ? editUser : null}
          organisation={client}
          wards={wards}
          onClose={() => setEditUser(null)}
          onSaved={loadData}
        />
      )}
    </Grid>
  )
}

export default HospitalPortalAccess
